import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Card, CardContent, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, Grid, Divider, Alert, FormControlLabel, Switch,
  InputAdornment, LinearProgress, Breadcrumbs, Link
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { contractsApi } from '../../services/api';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';
import { FIELD_SECTIONS, ALWAYS_VISIBLE } from '../../config/fieldVisibilityConfig';
import { useUser } from '../../context/UserContext';

const CONTRACT_TYPES = ['MSA', 'SOW', 'SLA', 'NDA', 'IRU', 'VENDOR', 'CUSTOMER', 'OTHER'];
const COUNTERPARTY_TYPES = ['customer', 'vendor', 'partner', 'other'];
const BILLING_FREQS = ['monthly', 'quarterly', 'annually', 'one-time'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
const PAYMENT_TERMS_OPTIONS = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90', 'Net 120'];

const DEFAULTS = {
  contract_name: '', contract_number: '', contract_type: 'MSA', status: 'active',
  counterparty_name: '', counterparty_type: 'customer', counterparty_contact_name: '',
  counterparty_contact_title: '', counterparty_contact_email: '', counterparty_company: '', address: '',
  counterparty_signatory_name: '', counterparty_signatory_title: '', counterparty_signature_date: '',
  hyperlink_signatory_name: '', hyperlink_signatory_title: '', hyperlink_signature_date: '',
  effective_date: '', expiration_date: '', execution_date: '', start_date: '', end_date: '',
  term_length_months: '', auto_renews: false, renewal_term_months: '', notice_period_days: '30',
  renewal_notes: '', renewal_terms: '',
  contract_value: '', currency: 'USD', billing_frequency: '', payment_terms_days: '',
  payment_terms: '', billing_milestones: '', late_fees_pct: '', taxes_pass_through: '',
  credits_penalties_refunds: '',
  governing_law: '', jurisdiction: '', venue_jurisdiction: '', assignment_rights: '',
  amendment_requirements: '',
  department: '', legal_entity: '', description: '', scope_summary: '', tags: ''
};

export default function CreateContract() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && !window.location.pathname.endsWith('/new');
  const { effectiveRole, loading: userLoading } = useUser();

  // Block viewers from reaching the create/edit form via direct URL
  useEffect(() => {
    if (!userLoading && effectiveRole === 'viewer') {
      navigate('/contracts', { replace: true });
    }
  }, [effectiveRole, userLoading, navigate]);

  const [form, setForm] = useState<any>(DEFAULTS);
  const [ownerInfo, setOwnerInfo] = useState<{ name: string; email: string; department: string; job_title: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const { isVisible } = useFieldVisibility(form.contract_type);

  useEffect(() => {
    if (isEdit && id) {
      contractsApi.get(id).then(res => {
        const c = res.data;
        setForm({
          ...DEFAULTS, ...c,
          auto_renews: !!c.auto_renews,
          contract_value: c.contract_value ?? '',
          term_length_months: c.term_length_months ?? '',
          renewal_term_months: c.renewal_term_months ?? '',
          notice_period_days: c.notice_period_days ?? '30',
          payment_terms_days: c.payment_terms_days ?? '',
          late_fees_pct: c.late_fees_pct ?? '',
          effective_date: c.effective_date || '',
          expiration_date: c.expiration_date || '',
          execution_date: c.execution_date || '',
          start_date: c.start_date || '',
          end_date: c.end_date || '',
        });
        if (c.owner_name || c.owner_email) {
          setOwnerInfo({
            name: c.owner_name || '',
            email: c.owner_email || '',
            department: c.owner_department || '',
            job_title: c.owner_job_title || '',
          });
        }
        setLoadingData(false);
      }).catch(() => { setError('Failed to load contract'); setLoadingData(false); });
    }
  }, [isEdit, id]);

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    setForm((prev: any) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Compute renewal notice date from expiration_date and notice_period_days
  const computedRenewalNoticeDate = useMemo(() => {
    if (!form.expiration_date || !form.notice_period_days) return '';
    const exp = new Date(form.expiration_date);
    exp.setDate(exp.getDate() - parseInt(form.notice_period_days));
    return exp.toISOString().split('T')[0];
  }, [form.expiration_date, form.notice_period_days]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

      if (isEdit && id) {
        await contractsApi.update(id, payload);
        navigate(`/contracts/${id}`);
      } else {
        const res = await contractsApi.create(payload);
        navigate(`/contracts/${res.data.id}`);
      }
    } catch (err: any) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors && apiErrors.length > 0) {
        // Show each validation error on its own line, prefixed with the field name
        // so the user knows which input to fix.
        const lines = apiErrors.map((e: any) => {
          const field = e.path || e.param || 'field';
          // Friendly-format the field name: counterparty_type -> "counterparty type"
          const friendly = String(field).replace(/_/g, ' ');
          return `• ${friendly}: ${e.msg}`;
        });
        setError(lines.join('\n'));
      } else {
        setError(err.response?.data?.error || 'Failed to save contract');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) return <LinearProgress />;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Header */}
      <Box mb={3}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/contracts')}>
            Contracts
          </Link>
          <Typography color="text.primary">{isEdit ? 'Edit Contract' : 'New Contract'}</Typography>
        </Breadcrumbs>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{isEdit ? 'Edit Contract' : 'Create Contract'}</Typography>
          <Box display="flex" gap={1}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined">Back</Button>
            <Button type="submit" startIcon={<Save />} variant="contained" disabled={loading}>
              {loading ? 'Saving...' : 'Save Contract'}
            </Button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Contract Identity */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Contract Identity</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField required fullWidth label="Contract Name" name="contract_name" value={form.contract_name} onChange={handleChange} />
                </Grid>
                {isVisible('contract_number') && <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Contract Number" name="contract_number" value={form.contract_number} onChange={handleChange} placeholder="e.g. MSA-2024-001" />
                </Grid>}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Contract Type</InputLabel>
                    <Select name="contract_type" value={form.contract_type} label="Contract Type" onChange={handleChange}>
                      {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select name="status" value={form.status} label="Status" onChange={handleChange}>
                      {['draft', 'active', 'expired', 'terminated', 'suspended'].map(s => (
                        <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {isVisible('department') && <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Department" name="department" value={form.department} onChange={handleChange} />
                </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Counterparty */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Counterparty</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField required fullWidth label="Counterparty Name" name="counterparty_name" value={form.counterparty_name} onChange={handleChange} />
                </Grid>
                {isVisible('counterparty_type') && <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Counterparty Type</InputLabel>
                    <Select name="counterparty_type" value={form.counterparty_type} label="Counterparty Type" onChange={handleChange}>
                      {COUNTERPARTY_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>}
                {isVisible('counterparty_company') && <Grid item xs={12}>
                  <TextField fullWidth label="Company Name" name="counterparty_company" value={form.counterparty_company} onChange={handleChange} placeholder="Legal entity / DBA name" />
                </Grid>}
                {isVisible('counterparty_contact_name') && <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Contact Name" name="counterparty_contact_name" value={form.counterparty_contact_name} onChange={handleChange} />
                </Grid>}
                {isVisible('counterparty_contact_title') && <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Contact Title" name="counterparty_contact_title" value={form.counterparty_contact_title} onChange={handleChange} />
                </Grid>}
                {isVisible('counterparty_contact_email') && <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Contact Email" name="counterparty_contact_email" type="email" value={form.counterparty_contact_email} onChange={handleChange} />
                </Grid>}
                {isVisible('address') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Address" name="address" value={form.address} onChange={handleChange} />
                </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Dates & Terms */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Dates & Terms</Typography>
              <Grid container spacing={2}>
                {isVisible('execution_date') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Execution Date" name="execution_date" type="date" value={form.execution_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>}
                {isVisible('effective_date') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Effective Date" name="effective_date" type="date" value={form.effective_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>}
                {isVisible('start_date') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Start Date" name="start_date" type="date" value={form.start_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>}
                {isVisible('expiration_date') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Expiration Date" name="expiration_date" type="date" value={form.expiration_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>}
                {isVisible('end_date') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="End Date" name="end_date" type="date" value={form.end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>}
                {isVisible('term_length_months') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Term Length (months)" name="term_length_months" type="number" value={form.term_length_months} onChange={handleChange} />
                </Grid>}
                {isVisible('notice_period_days') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Notice Period (days)" name="notice_period_days" type="number" value={form.notice_period_days} onChange={handleChange} />
                </Grid>}
                {isVisible('notice_period_days') && <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Renewal Notice Date" value={computedRenewalNoticeDate}
                    InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
                    helperText="Auto-computed from expiration − notice period"
                  />
                </Grid>}
                {isVisible('auto_renews') && <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch name="auto_renews" checked={form.auto_renews} onChange={handleChange} />}
                    label="Auto-Renews"
                  />
                </Grid>}
                {isVisible('renewal_term_months') && form.auto_renews && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Renewal Term (months)" name="renewal_term_months" type="number" value={form.renewal_term_months} onChange={handleChange} />
                  </Grid>
                )}
                {isVisible('renewal_terms') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Renewal Terms" name="renewal_terms" value={form.renewal_terms} onChange={handleChange} placeholder="Describe renewal conditions, options, or restrictions" />
                </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Terms */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Financial Terms</Typography>
              <Grid container spacing={2}>
                {isVisible('contract_value') && <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Contract Value" name="contract_value" type="number" value={form.contract_value} onChange={handleChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>}
                {isVisible('currency') && <Grid item xs={6} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select name="currency" value={form.currency} label="Currency" onChange={handleChange}>
                      {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>}
                {isVisible('payment_terms_days') && <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Payment Terms (days)" name="payment_terms_days" type="number" value={form.payment_terms_days} onChange={handleChange} />
                </Grid>}
                {isVisible('payment_terms') && <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Terms</InputLabel>
                    <Select name="payment_terms" value={form.payment_terms} label="Payment Terms" onChange={handleChange}>
                      <MenuItem value="">-</MenuItem>
                      {PAYMENT_TERMS_OPTIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>}
                {isVisible('billing_frequency') && <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Billing Frequency</InputLabel>
                    <Select name="billing_frequency" value={form.billing_frequency} label="Billing Frequency" onChange={handleChange}>
                      <MenuItem value="">-</MenuItem>
                      {BILLING_FREQS.map(f => <MenuItem key={f} value={f} sx={{ textTransform: 'capitalize' }}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>}
                {isVisible('billing_milestones') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Billing Milestones" name="billing_milestones" value={form.billing_milestones} onChange={handleChange} placeholder="e.g. 50% on signing, 50% on delivery" />
                </Grid>}
                {isVisible('late_fees_pct') && <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth label="Late Fees %" name="late_fees_pct" type="number" value={form.late_fees_pct} onChange={handleChange}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                </Grid>}
                {isVisible('taxes_pass_through') && <Grid item xs={12} sm={8}>
                  <TextField fullWidth label="Taxes / Pass-Through Rules" name="taxes_pass_through" value={form.taxes_pass_through} onChange={handleChange} />
                </Grid>}
                {isVisible('credits_penalties_refunds') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Credits, Penalties & Refunds" name="credits_penalties_refunds" value={form.credits_penalties_refunds} onChange={handleChange} />
                </Grid>}
                {isVisible('legal_entity') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="DBA / Trade Name" name="legal_entity" value={form.legal_entity} onChange={handleChange} />
                </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Legal & Governance */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Legal &amp; Governance</Typography>
              <Grid container spacing={2}>
                {isVisible('governing_law') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Governing Law" name="governing_law" value={form.governing_law} onChange={handleChange} placeholder="e.g. State of Delaware" />
                </Grid>}
                {isVisible('jurisdiction') && <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Jurisdiction" name="jurisdiction" value={form.jurisdiction} onChange={handleChange} />
                </Grid>}
                {isVisible('venue_jurisdiction') && <Grid item xs={12}>
                  <TextField fullWidth label="Venue / Jurisdiction" name="venue_jurisdiction" value={form.venue_jurisdiction} onChange={handleChange} placeholder="e.g. Courts of New York County" />
                </Grid>}
                {isVisible('assignment_rights') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Assignment Rights" name="assignment_rights" value={form.assignment_rights} onChange={handleChange} placeholder="e.g. Neither party may assign without prior written consent" />
                </Grid>}
                {isVisible('amendment_requirements') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Amendment Requirements" name="amendment_requirements" value={form.amendment_requirements} onChange={handleChange} placeholder="e.g. Amendments must be in writing and signed by both parties" />
                </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Owner Info */}
        {ownerInfo && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Contract Owner</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Owner Name" value={ownerInfo.name} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Owner Email" value={ownerInfo.email} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Department / Company" value={ownerInfo.department} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Title" value={ownerInfo.job_title} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Signatures */}
        {(isVisible('counterparty_signatory_name') || isVisible('hyperlink_signatory_name')) && <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Signatures</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Counterparty Signatory</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Name" name="counterparty_signatory_name" value={form.counterparty_signatory_name} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Title" name="counterparty_signatory_title" value={form.counterparty_signatory_title} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Signature Date" name="counterparty_signature_date" type="date" value={form.counterparty_signature_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>HyperLink Signatory</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Name" name="hyperlink_signatory_name" value={form.hyperlink_signatory_name} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Title" name="hyperlink_signatory_title" value={form.hyperlink_signatory_title} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Signature Date" name="hyperlink_signature_date" type="date" value={form.hyperlink_signature_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>}

        {/* Description & Scope */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Description &amp; Scope</Typography>
              <Grid container spacing={2}>
                {isVisible('description') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Description" name="description" value={form.description} onChange={handleChange} />
                </Grid>}
                {isVisible('scope_summary') && <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Scope Summary" name="scope_summary" value={form.scope_summary} onChange={handleChange} />
                </Grid>}
                {isVisible('tags') && <Grid item xs={12}>
                  <TextField fullWidth label="Tags (comma-separated)" name="tags" value={form.tags} onChange={handleChange} placeholder="renewal, high-value, strategic" />
                </Grid>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading} startIcon={<Save />} size="large">
              {loading ? 'Saving...' : (isEdit ? 'Update Contract' : 'Create Contract')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
